import { createAsyncThunk } from '@reduxjs/toolkit';
import { loginUser, signupUser, forgotPassword, resetPassword, getUserProfile, updateUserProfile, changePassword, verifyEmail, resendVerificationCode, firebaseLogin } from '../../services/authService';

// Login thunk
export const loginUserThunk = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await loginUser(credentials);


      // Transform the API response to match our Redux state structure
      const userData = {
        user: {
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          profilePicture: response.user?.profilePicture?.url || null,
          phone: response.user?.phone || null,
          company: response.user?.company || null,
          gender: response.user?.gender || null,
          country: response.user?.country || null,
          id: response.user._id,
          email: response.user.email,
          tokens: response.user.tokens,
          tokenExpiration: response.user.tokenExpiration || null,
          apiKeys: response.apiKey,
          purchaseHistory: response.user.purchaseHistory,
        },
        role: response.user.role.toLowerCase(), // Convert "USER" to "user"
        token: response.token, // Use the first API key as token
        apiKeys: response.user.apiKeys?.[0]?.key || null,
      };

      return userData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Signup thunk
export const signupUserThunk = createAsyncThunk(
  'auth/signupUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await signupUser(userData);

      // Check if email verification is required
      if (response.requiresVerification) {
        return {
          requiresVerification: true,
          email: response.email,
          message: response.message
        };
      }

      // Old flow - user is logged in immediately (backward compatibility)
      const transformedData = {
        user: {
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          profilePicture: response.user.profilePicture?.url || null,
          phone: response.user.phone || null,
          company: response.user.company || null,
          id: response.user._id,
          email: response.user.email,
          tokens: response.user.tokens,
          tokenExpiration: response.user.tokenExpiration || null,
          apiKeys: response.apiKey,
          purchaseHistory: response.user.purchaseHistory,
        },
        role: response.user.role.toLowerCase(), // Convert "USER" to "user"
        token: response.token, // Use the first API key as token
        apiKeys: response.user.apiKeys?.[0]?.key || null,
      };

      return transformedData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Forgot password thunk
export const forgotPasswordThunk = createAsyncThunk(
  'auth/forgotPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await forgotPassword(email);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Reset password thunk
export const resetPasswordThunk = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const response = await resetPassword(token, newPassword);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Get user profile thunk
export const getUserProfileThunk = createAsyncThunk(
  'auth/getUserProfile',
  async (token, { rejectWithValue }) => {
    try {
      const response = await getUserProfile(token);

      const transformedData = {
        user: {
          id: response._id.$oid,
          email: response.email,
          tokens: response.tokens,
          tokenExpiration: response.tokenExpiration || null,
          apiKeys: response.apiKeys,
          purchaseHistory: response.purchaseHistory,
        },
        role: response.role.toLowerCase(),
      };

      return transformedData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update user profile thunk
export const updateUserProfileThunk = createAsyncThunk(
  'auth/updateUserProfile',
  async ({ token, profileData }, { rejectWithValue }) => {
    try {
      const response = await updateUserProfile(token, profileData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Change password thunk
export const changePasswordThunk = createAsyncThunk(
  'auth/changePassword',
  async ({ token, passwordData }, { rejectWithValue }) => {
    try {
      const response = await changePassword(token, passwordData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Verify email thunk
export const verifyEmailThunk = createAsyncThunk(
  'auth/verifyEmail',
  async ({ email, code }, { rejectWithValue }) => {
    try {
      const response = await verifyEmail(email, code);
      // Transform the API response to match our Redux state structure
      const userData = {
        user: {
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          profilePicture: response.user?.profilePicture?.url || null,
          phone: response.user?.phone || null,
          company: response.user?.company || null,
          id: response.user._id,
          email: response.user.email,
          tokens: response.user.tokens,
          tokenExpiration: response.user.tokenExpiration || null,
          apiKeys: response.apiKey,
          purchaseHistory: response.user.purchaseHistory,
        },
        role: response.user.role.toLowerCase(),
        token: response.token,
        apiKeys: response.user.apiKeys?.[0]?.key || null,
      };

      return userData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Resend verification code thunk
export const resendVerificationCodeThunk = createAsyncThunk(
  'auth/resendVerificationCode',
  async (email, { rejectWithValue }) => {
    try {
      const response = await resendVerificationCode(email);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Firebase login thunk
export const firebaseLoginThunk = createAsyncThunk(
  'auth/firebaseLogin',
  async (idToken, { rejectWithValue }) => {
    try {
      const response = await firebaseLogin(idToken);

      // Transform the API response to match our Redux state structure
      const userData = {
        user: {
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          profilePicture: response.user?.profilePicture?.url || null,
          phone: response.user?.phone || null,
          company: response.user?.company || null,
          gender: response.user?.gender || null,
          country: response.user?.country || null,
          id: response.user._id,
          email: response.user.email,
          tokens: response.user.tokens,
          tokenExpiration: response.user.tokenExpiration || null,
          apiKeys: response.apiKey,
          purchaseHistory: response.user.purchaseHistory,
        },
        role: response.user.role.toLowerCase(), // Convert "USER" to "user"
        token: response.token, // JWT token
        apiKeys: response.user.apiKeys?.[0]?.key || null,
        isNewUser: response.isNewUser || false,
      };

      return userData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
